/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/icon_tick_gray.svg";
import __asset_1 from "@res/icons/icon_tick_green.svg";
import __asset_2 from "@res/icons/icon_tick_red.svg";
import __asset_3 from "@res/icons/icon_thumb_down_red.svg";
import __asset_4 from "@res/icons/icon_thumb_up_green.svg";
import __asset_5 from "@res/icons/icon_heart.svg";
import __asset_6 from "@res/icons/icon_thumb_side_gray.svg";
import __asset_7 from "@res/icons/no_data.svg";
import __asset_8 from "@res/icons/icon_compose_message.svg";
import * as React from "react";
import {useContext, useEffect} from "react";
import {BaseComponent, Dialog_Builder, RoutingModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {DB_Patient} from "@app/app-shared/patient";
import {TabButton} from "../../widget/TabButton";
import {AlertButton} from "../alerts/AlertButton";
import {AlertBadge} from "../alerts/AlertBadge";
import {ICONS} from "@res/icons";

import {FONTS} from "@res/fonts";
import {PARAM_PATIENT, UserPreferencesModule} from "@modules/UserPreferencesModule";
import {Route_CarePlan, Route_Dashboard, Route_Default, Route_Hedis, Route_PatientsHome, Route_WellnessCoach} from "../../../Routes";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {LastUpdated, navigateToPatientPage} from "@modules/PatientDataModule";
import {padding_rl_lg, padding_tb_lg, Spacing} from "@styles/styles";
import {MessagePatientButton} from "../../widget/MessagePatientButton";
import {ComposeMessageDialog} from "../messages/ComposeMessageDialog";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {Api_GetPcpContact} from "@app/ir-q-app-common/types/db-contact";
import * as emotion from "emotion";
import {DateTimesUtil} from "./DateTimesUtil";
import {Appointment} from "./Appointment";
import {AddMedicationReminderButton} from "../reminders/AddMedicationReminderButton";
import {_setInterval} from "@intuitionrobotics/ts-common";
import {hasUserOrgRoles} from "@modules/utils";
import {messagingRoles, OrgRoleLevel} from "@app/app-shared/permission";
import {PatientContextProvider, PatientsContext} from "./PatientsContext";

import {useSelectedPatient} from "../../PatientProvider";
import {RemindersModule} from "@modules/RemindersModule";
import {OrganizationsContext} from "../organizations/OrganizationsContext";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {ContactsContext, ContactsMap} from "../messages/ContactsContext";
import {GoalsConfigModule} from "@modules/GoalsConfigModule";

export const icon_tick_gray = __asset_0;
export const icon_tick_green = __asset_1;
export const icon_tick_red = __asset_2;
export const icon_thumb_down = __asset_3;
export const icon_thumb_up_green = __asset_4;
export const icon_heart = __asset_5;
export const icon_thumb_side_gray = __asset_6;
export const no_data = __asset_7;
export const composeMessage = __asset_8;

const extraInfo = emotion.css(
    {
        marginRight: 30
    }
);

type Props = {
    patient: DB_Patient,
    org: DB_Organization,
    contacts: ContactsMap
    demo: boolean
}


type State = {
    currentTime?: string,
    hasPcp: boolean | null,
}

export const PatientTab = () => {
    const patient = useSelectedPatient();
    if (!patient)
        return null;

    const {organizations} = useContext(OrganizationsContext);
    const {contacts} = useContext(ContactsContext);
    const {demo} = useContext(PatientsContext)
    useEffect(() => {
        const org = organizations[patient.organizationId];
        if (demo)
            return;

        if (Object.keys(contacts).length)
            RemindersModule.fetchRemindersForPatient(contacts, org, patient);

        GoalsConfigModule.list(patient.unitId);
    }, [contacts, patient, demo, organizations]);

    return <PatientsTabInner demo={demo} patient={patient} org={organizations[patient.organizationId]} contacts={contacts}/>;
}


class PatientsTabInner
    extends BaseComponent<Props, State>
    implements LastUpdated {

    constructor(props: Props) {
        super(props);
        this.state = {
            currentTime: DateTimesUtil.timePresentation(this.props.patient.timezone),
            hasPcp: null,
        };
    }

    __lastUpdated = () => this.forceUpdate();
    __onUnitsLoaded = () => this.forceUpdate();

    private TIME_POLL_INTERVAL = 5000;

    private timer?: number

    private renderPatientName = () => {
        const selectedPatient = this.props.patient;
        return (
            <>
                <div style={{height: 25, width: 1, margin: "0 16px", backgroundColor: COLORS.neutral_gray_1}}/>
                {
                    FONTS.medium_21(
                        selectedPatient.name,
                        {
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: 400
                        })
                }
            </>
        );
    };

    private renderPatientUnitId = (selectedPatient: DB_Patient) => {
        return FONTS.reg_16_black(
            selectedPatient.unitId,
            {
                overflowX: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: 400,
                margin: "0 16px"
            }
        );
    };

    private fetchPcpContact = (unitId: string) => {
        XhrHttpModule
            .createRequest<Api_GetPcpContact>(HttpMethod.GET, "get-pcp-contact")
            .setRelativeUrl(`/v2/contacts/get-pcp?agentId=${unitId}`)
            .setOnError(() => this.setState({hasPcp: false}))
            .execute((contact) => this.setState({hasPcp: !!contact}));
    };

    componentDidMount() {
        this.fetchPcpContact(this.props.patient.unitId);
        this.timer = _setInterval(() => {
            const _patient = this.props.patient;
            if (!_patient) {
                this.setState({currentTime: undefined});
                return;
            }

            this.setState({currentTime: DateTimesUtil.timePresentation(_patient.timezone)});
        }, this.TIME_POLL_INTERVAL);
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.patient.unitId !== this.props.patient.unitId)
            this.fetchPcpContact(this.props.patient.unitId);
    }

    componentWillUnmount() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = undefined;
    }

    private renderHeader = (patient: DB_Patient) => {
        return <div
            className={"match_width"}
            style={{
                height: 60,
                backgroundColor: COLORS.white,
                boxShadow: `0 1px 8px 0 ${COLORS.box_shadow}`,
                marginTop: "1px", zIndex: 3
            }}
        >
            <div className={`ll_h_c match_height ${padding_rl_lg}`}>
                {this.renderLeftHeader()}
                {this.renderCenterHeader(patient)}
                {this.renderRightHeader(patient)}
            </div>
        </div>;
        /* end of subheader */
    };

    private renderRightHeader = (patient: DB_Patient) => {
        return <div className={"ll_h_c match_height"} style={{
            flexBasis: "0%", flexGrow: 1, boxSizing: "border-box", justifyContent: "flex-end"
        }}>
            {/* Right */}
            <div/>
            {this.renderBirthday()}
            {this.renderCurrentTime()}
            {this.renderNextAppointment()}
            {this.renderMedicationReminder()}
            {hasUserOrgRoles(patient.organizationId, messagingRoles) && this.renderSendMessage()}
        </div>;
    };

    private renderCenterHeader = (patient: DB_Patient) => {
        return <div className={"ll_h_c match_height"} style={{boxSizing: "border-box"}}>
            {/* Always centered, no matter left or right width */}
            {this.renderPatientName()}
            {this.renderPatientUnitId(patient)}
            {this.renderAlertBadge(patient)}
            {this.renderUnpairedBadge(patient)}
        </div>;
    };

    private renderLeftHeader = () => {
        const routeKey = UserPreferencesModule.getLastMainRoute() || Route_PatientsHome;
        const routeString = routeKey[0].toUpperCase() + routeKey.slice(1);
        return <div className={"ll_h_c match_height"} style={{boxSizing: "border-box"}}>
            {/* Left */}
            <div className={"ll_h_c clickable"} style={{marginLeft: -7}} onClick={() => {
                RoutingModule.goToRoute(routeKey);
            }}>
                {ICONS.icon_arrowhead_dark_l(COLORS.neutral_text_dark_2)}
                {FONTS.light_21(`${routeString === "Alerts" ? "Notifications" : routeString}`, {marginLeft: 20})}
            </div>
        </div>;
    };

    private renderSendMessage = () => {
        const selectedPatient = this.props.patient;
        if (!selectedPatient)
            return;

        return <div>
            <MessagePatientButton text={"Message"} textStyle={{whiteSpace: "nowrap"}} enabled={true} onClick={() => {
                new Dialog_Builder(<ComposeMessageDialog preselectedPatient={selectedPatient}/>)
                    .addStyle({backgroundColor: "unset", boxShadow: "unset"})
                    .setZIndex(100)
                    .show();
            }}/>
        </div>;
    };

    private renderBirthday = () => {
        const selectedPatient = this.props.patient;
        const contact = this.props.contacts[selectedPatient?.contactId || ""];
        if (!contact || !contact.birthday)
            return;

        return (
            <div
                className={`ll_h_c ${extraInfo}`}
                onMouseEnter={(e) => {
                    showTooltip(
                        e,
                        "This is the customer's date of birth",
                        e.currentTarget.getBoundingClientRect().left,
                        e.currentTarget.getBoundingClientRect().bottom + 10
                    );
                }}
                onMouseLeave={() => hideTooltip()}
            >
                {ICONS.icon_birthday(COLORS.neutral_text_dark_2, 20)}
                {FONTS.reg_16_black(DateTimesUtil.birthdayPresentation(contact.birthday), {marginLeft: 15})}
            </div>
        );
    };

    private renderCurrentTime = () => {
        return this.state.currentTime && (
            <div
                className={`ll_h_c ${extraInfo}`}
                onMouseEnter={(e) => {
                    showTooltip(
                        e,
                        "This is the customer's current local time",
                        e.currentTarget.getBoundingClientRect().left,
                        e.currentTarget.getBoundingClientRect().bottom + 10
                    );
                }}
                onMouseLeave={() => hideTooltip()}
            >
                {/*{ICONS.icon_currentTime(COLORS.neutral_text_dark_2, 20)}*/}
                {FONTS.reg_16_black(this.state.currentTime)}
            </div>
        );
    };

    private renderNextAppointment = () => {
        const selectedPatient = this.props.patient;
        if (!selectedPatient)
            return;

        return <Appointment patient={selectedPatient}/>
    };

    private renderMedicationReminder = () => {
        const selectedPatient = this.props.patient;
        if (!selectedPatient)
            return;

        return <AddMedicationReminderButton patient={selectedPatient}/>
    }

    private renderAlertBadge = (patient: DB_Patient) => {
        return <div style={{marginLeft: 12}}><AlertButton
            patient={patient}
            onClick={() => navigateToPatientPage(patient, Route_PatientsHome)}
        /></div>
    };

    private renderUnpairedBadge = (patient: DB_Patient) => {
        if (!patient.disabled || this.props.demo)
            return;

        const css = emotion.css(
            {
                color: COLORS.white,
                minWidth: 58,
                height: 26,
                textAlign: 'center',
                borderRadius: 4,
                backgroundColor: COLORS.red,
                ':hover': {
                    boxShadow: 'rgb(236 236 236) 0px 0px 3px 1px'
                }
            }
        );

        return <div className={`${css} clickable ll_h_c`}
                    style={{justifyContent: 'center', paddingLeft: '3px', paddingRight: '3px', marginLeft: 12}}>
            {FONTS.reg_11_white("This patient has disenrolled permanently")}
        </div>;
    };

    private renderTabs = () => {
        return <div className={"ll_v_s"} style={{marginBottom: Spacing.m}}>
            <div className={"ll_h_c flex_center"} style={{height: Spacing.l}}>
                {/* Always centered, no matter left or right width */}
                {this.renderTabCenter()}
            </div>
        </div>;
    };

    private isMetroPlus = () => {
        return this.props.org?.name?.toLowerCase()?.includes("metroplus") ?? false;
    }

    private renderTabCenter = () => {
        const selectedKey = RoutingModule.getMyRouteKey() || Route_Default;
        return <div className={"ll_h_c"} style={{zIndex: 200}}>
            {this.renderDashboardTab(selectedKey)}
            {this.renderCareConfigTab(selectedKey)}
            {this.renderHedisTab(selectedKey)}
            {this.renderWellnessCoachTab(selectedKey)}
        </div>;
    };

    private renderCareConfigTab = (selectedKey: string) => {
        return <TabButton
            title={"Care Plan"}
            selected={selectedKey === Route_CarePlan}
            onClick={() => {
                AnalyticsModule.logCareManagerEvent({
                    objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
                    entity: AnalyticConsts.Entities.USER,
                    activityStatus: AnalyticConsts.ActivityStatus.CLICKED,
                    objectSubType: AnalyticConsts.ObjectSubTypes.CARE_PLAN
                });
                RoutingModule.goToRoute(Route_CarePlan, {[PARAM_PATIENT]: this.props.patient._id});
            }}
        />;
    };

    private renderHedisTab = (selectedKey: string) => {
        if (!this.isMetroPlus())
            return <></>;

        const noPcp = this.state.hasPcp === false;
        return (
            <div
                onMouseEnter={noPcp && !this.props.demo ? (e) => showTooltip(
                    e,
                    "To enable HEDIS for this user, please add his PCP contact",
                    e.currentTarget.getBoundingClientRect().left,
                    e.currentTarget.getBoundingClientRect().bottom + 10
                ) : undefined}
                onMouseLeave={noPcp && !this.props.demo ? () => hideTooltip() : undefined}
            >
                <TabButton
                    title={"HEDIS"}
                    selected={selectedKey === Route_Hedis}
                    disabled={noPcp && !this.props.demo}
                    onClick={() => RoutingModule.goToRoute(Route_Hedis, {[PARAM_PATIENT]: this.props.patient._id})}
                />
            </div>
        );
    }

    private renderDashboardTab = (selectedKey: string) => {
        const patient = this.props.patient;
        return <AlertBadge
            top={2}
            right={Spacing.m}
            patient={patient}
        >
            <TabButton
                title={"Dashboard"}
                selected={selectedKey === Route_Dashboard}
                onClick={() => {
                    navigateToPatientPage(patient, Route_PatientsHome);
                }}
            />
        </AlertBadge>;
    };

    private renderWellnessCoachTab = (selectedKey: string) => {
        const patientId = this.props.patient._id;
        return <TabButton
            title={"Wellness Program"}
            selected={selectedKey === Route_WellnessCoach}
            onClick={() => {
                AnalyticsModule.logCareManagerEvent({
                    objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
                    entity: AnalyticConsts.Entities.USER,
                    activityStatus: AnalyticConsts.ActivityStatus.CLICKED,
                    objectSubType: AnalyticConsts.ObjectSubTypes.WELLNESS_PROGRAM
                });

                RoutingModule.goToRoute(Route_WellnessCoach, {[PARAM_PATIENT]: patientId});
            }}
        />;
    };

    private renderBody = () => (<div className={"ll_v_s"} style={{flex: 1, overflowY: "auto", zIndex: 2}}>
        <div className={`ll_v_s ${padding_rl_lg} ${padding_tb_lg}`} style={{flex: 1, position: "relative"}}>
            {/*<TabBodyLoader/>*/}
            {this.renderTabs()}
            {RoutingModule.getRoutesMap(
                [
                    Route_Dashboard,
                    Route_CarePlan,
                    Route_Hedis,
                    Route_WellnessCoach
                ])}
        </div>
    </div>);

    render() {
        const patient = this.props.patient;
        return <PatientContextProvider roles={[OrgRoleLevel.USER_DATA]}>{
            () => {
                return <div className={"ll_v_s"} style={{flex: 1, overflowX: "scroll"}}>
                    {this.renderHeader(patient)}
                    {this.renderBody()}
                </div>
            }
        }</PatientContextProvider>
    }
}
